import {ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {SubscriberService} from "../../services/subscriber.service";
import {Subscription} from "rxjs";
import {SubscriptionStatus} from "../../model/subscription-status";
import {ModalsService} from "../../services/modals.service";
import {Router} from "@angular/router";
import {constants} from "../../constants";

declare var WaveSurfer: any;

@Component({
  selector: 'app-subscriber-ugc-page',
  templateUrl: './subscriber-ugc-page.component.html',
  styleUrls: ['./subscriber-ugc-page.component.css'],
  host: {
    class: 'app-page'
  }
})
export class SubscriberUgcPageComponent implements OnInit, OnDestroy {
  @ViewChild("fileUpload") fileUpload: ElementRef;
  subscribed: boolean;

  selectedFile: { file: File, name: string, size: number, start: number, duration: number };
  selectedFileError: string;

  termsAccepted: boolean;
  nextEnabled: boolean;
  fileSelected: boolean;

  subscription: Subscription;

  wavesurfer: any;
  wavesurferInitialized: boolean;
  wavesurferReady: boolean;
  playing: boolean;
  currentTime: string;
  duration: string;
  endTime: string;

  requestInProgress: boolean;
  privetPlus: boolean;
  isLoaded: boolean = false;

  constructor(private readonly subscriberService: SubscriberService,
              private readonly modalsService: ModalsService,
              private readonly router: Router,
              private readonly changeDetectorRef: ChangeDetectorRef) {
    this.subscribed = false;
    this.selectedFile = null;
    this.selectedFileError = null;

    this.termsAccepted = false
    this.nextEnabled = false
    this.fileSelected = false

    this.subscription = new Subscription();

    this.wavesurfer = null;
    this.wavesurferInitialized = false;
    this.wavesurferReady = false;
    this.playing = false;
    this.currentTime = null;
    this.duration = null;
    this.endTime = null;

    this.requestInProgress = false;
    this.privetPlus = false;
  }

  ngOnInit(): void {
    this.subscription.add(
      this.subscriberService.subscriber
        .subscribe(value => {
          if (value?.privetStatus !== undefined) {
            this.isLoaded = true;
            this.subscribed = value.privetStatus == SubscriptionStatus.ACTIVE || value.privetStatus == SubscriptionStatus.ACTIVATING;
            this.privetPlus = value.privetPlusStatus == SubscriptionStatus.ACTIVE || value.privetPlusStatus == SubscriptionStatus.ACTIVATING;
          }
        })
    );
  }

  @ViewChild("trackFragment") set trackFragment(value: ElementRef) {
    this.destroyWaveSurfer();

    if (value) {
      this.initWaveSurfer();
    }
  }

  onFileSelected(event: any): void {
    const file: File = event.target.files[0];
    const name = file.name;
    const size = Math.round(file.size / 100000) / 10;

    this.selectedFile = {
      file: file,
      name: name,
      size: size,
      start: 0,
      duration: 30
    }

    this.updateNextButtonActivity();
  }

  clearSelectedFile(): void {
    this.selectedFile = null;
    this.fileUpload.nativeElement.value = null;

    this.updateNextButtonActivity();
  }

  updateNextButtonActivity(): void {
    let fileValid = !!this.selectedFile;
    this.selectedFileError = null;

    if (this.selectedFile) {
      let fileSizeKb = this.selectedFile.file.size / 1000;
      if (fileSizeKb < 15 || fileSizeKb > 15000) {
        fileValid = false;
        this.selectedFileError = 'subscriber-ugc-page.wrong-size';
      }

      if (!this.selectedFile.file.name.endsWith('.mp3')) {
        fileValid = false;
        this.selectedFileError = 'subscriber-ugc-page.wrong-file';
      }
    }

    this.nextEnabled = this.termsAccepted && fileValid;
  }

  showFragmentSelector(): void {
    if (this.nextEnabled) {
      this.fileSelected = true;
    }
  }

  showFileSelector(): void {
    this.fileSelected = false;
  }

  initWaveSurfer(): void {
    this.wavesurferInitialized = false;
    this.playing = false;

    this.wavesurfer = WaveSurfer.create({
      container: "[data-track-cut-preview-wave]",
      waveColor: "#DFE0E5",
      progressColor: "#DFE0E5",
      height: 296,
      mediaControls: true,
      responsive: true,
      normalize: true,
      fillParent: true,
      plugins: [WaveSurfer.regions.create({}), WaveSurfer.cursor.create({})],
    });

    this.addRange();

    this.loadFile();

    this.wavesurfer.on("ready", () => {
      this.wavesurferReady = true;
      this.currentTime = this.transformTime(this.wavesurfer.getCurrentTime());
      this.duration = this.transformTime(this.wavesurfer.getDuration());
      this.endTime = this.transformTime(30);

      this.wavesurferInitialized = true;
      setTimeout(() => {
        this.setPreviewValues();
        this.setCursorPosition();
      }, 5);
    });

    this.wavesurfer.on("audioprocess", () => {
      this.changeDetectorRef.detectChanges();

      let currentTime = this.wavesurfer.getCurrentTime();

      this.currentTime = this.transformTime(currentTime);

      this.setCursorPosition();
      this.setPreviewValues();

      if (!this.playing) {
        this.playing = true;
      }
    });

    this.wavesurfer.on("pause", () => {
      this.changeDetectorRef.detectChanges();
      this.playing = false;
    });

    this.wavesurfer.on("region-out", () => {
      this.wavesurfer.pause();
      this.changeDetectorRef.detectChanges();
      this.playing = false;
    });

    this.wavesurfer.on("region-update-end", () => {
      this.setPreviewValues();
    });

    this.wavesurfer.on("region-updated", () => {
      this.setPreviewValues();
    });

    this.wavesurfer.on("seek", () => {
      this.setCursorPosition();
    })
  }

  private addRange(): void {
    let reg = this.wavesurfer.addRegion({
      id: "range",
      start: 0,
      end: 30,
      minLength: constants.minUgcDuration,
      maxLength: constants.maxUgcDuration,
      drag: true,
      resize: true,
    });
    reg.element.children[0].style.width = "14px";
    reg.element.children[1].style.width = "14px";
  }

  private loadFile(): void {
    let reader = new FileReader();

    reader.onload = (evt) => {
      // @ts-ignore
      let blob = new window.Blob([new Uint8Array(evt.target.result)]);
      this.wavesurfer.loadBlob(blob);
    };

    reader.onerror = (evt) => {
      console.error("An error ocurred reading the file: ", evt);
    };

    reader.readAsArrayBuffer(this.selectedFile.file);
  }

  private transformTime(time: number): string {
    const minutes = parseInt("" + time / 60, 10);
    const seconds = parseInt("" + (time % 60), 10);

    return (
      minutes +
      ":" +
      (seconds < 10 ? "0" + seconds : seconds < 60 ? seconds : 0)
    );
  }

  setCursorPosition(): void {
    let labelCurrent = document.querySelector("[data-track-cut-current]");
    if (labelCurrent instanceof HTMLElement) {
      let trackContainer = document.querySelector('.track-cut__preview-container');
      let trackWidth = trackContainer.clientWidth;
      let cursorPosition = this.wavesurfer.getCurrentTime() / this.wavesurfer.getDuration();
      let cursorLeft = cursorPosition * trackWidth;
      labelCurrent.style.left = cursorLeft + "px";
      labelCurrent.textContent = this.transformTime(this.wavesurfer.getCurrentTime());
    }
  }

  private setPreviewValues() {
    if (!this.wavesurfer.regions.list.range.end) {
      this.wavesurfer.regions.list.range.end = 30
    }

    let start = Math.round(this.wavesurfer.regions.list.range.start);
    let end = Math.round(this.wavesurfer.regions.list.range.end);

    let labelStart = document.querySelector("[data-track-cut-preview-start]");
    let labelEnd = document.querySelector("[data-track-cut-preview-end]");


    let rangeEl = this.wavesurfer.regions.list.range.element;
    let regionStart = rangeEl.offsetLeft;
    let regionWidth = rangeEl.clientWidth;

    if (labelStart instanceof HTMLElement) {
      labelStart.style.left = regionStart + "px";
      labelStart.textContent = this.transformTime(start);
    }

    if (labelEnd instanceof HTMLElement) {
      labelEnd.style.left = regionStart + regionWidth + 7 + "px";
      labelEnd.textContent = this.transformTime(end);
    }

    if (end - start > constants.maxUgcDuration) {
      end = start + constants.maxUgcDuration;
      this.wavesurfer.regions.list.range.end = end;
    }

    this.selectedFile.start = start;
    this.selectedFile.duration = end - start;
  }

  togglePlay(): void {
    if (this.playing) {
      this.wavesurfer.pause();
    } else {
      let currentTime = this.wavesurfer.getCurrentTime();
      if (currentTime > this.selectedFile.start && currentTime < this.selectedFile.start + this.selectedFile.duration - 1) {
        this.wavesurfer.play()
      } else {
        this.wavesurfer.regions.list.range.play();
      }
    }
  }

  resetWaveSurfer(): void {
    if (this.wavesurfer) {
      this.wavesurfer.setCurrentTime(this.wavesurfer.regions.list.range.start);
      this.setPreviewValues();
    }
  }

  destroyWaveSurfer(): void {
    if (this.wavesurfer) {
      if (this.playing) {
        this.wavesurfer.pause();
        this.playing = false;
      }
      this.wavesurferInitialized = false;
      this.wavesurfer.destroy();
      this.wavesurfer = null;
      this.wavesurferReady = false;
    }
  }

  uploadTrack(): void {
    if (this.wavesurferReady && !this.requestInProgress && this.selectedFile) {
      this.wavesurfer.pause();
      this.requestInProgress = true;

      this.subscriberService.uploadUgc(this.selectedFile.file, this.selectedFile.start, this.selectedFile.duration)
        .subscribe(result => {
          this.requestInProgress = false;

          if (result == 'SUCCESS') {
            this.router.navigate(['subscriber', 'melodies']);
            this.modalsService.showUgcUploaded();
          } else {
            this.router.navigate(['error', '500']);
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.destroyWaveSurfer();
    this.subscription.unsubscribe();
  }
}
